/**
 * Authentication Service
 *
 * Bootstraps an anonymous session against /api/auth/token and keeps the
 * resulting bound session token in memory only (never in localStorage /
 * sessionStorage). The token is bound to the client's IP + User-Agent at
 * mint time and expires after 30 minutes — see `api/authToken/session.js`.
 *
 * The token is sent back to the proxy as the `x-daisy-session` header.
 * This header path is required because the widget is embedded in cross-
 * site iframes (`*.nsw.gov.au`, `*.opencities.com`, …) where Safari ITP
 * and Chrome's third-party cookie phase-out block the `daisy_session`
 * cookie. The cookie is still set (via `Set-Cookie`) and used on first-
 * party contexts — both paths carry the same bound token.
 *
 * On 401 from the proxy, the caller should invoke `reinitialize()` to
 * mint a fresh token before retrying.
 */

import { configService } from '../config/environment.js';
import { sessionStore, STORAGE_KEYS } from '../utils/storage.js';
import { logger } from '../utils/logger.js';

class AuthService {
    constructor() {
        this._initialized = false;
        this._isInitializing = false;
        this._entraOrgId = null;
        this._sessionToken = null;
    }

    get isInitialized() {
        return this._initialized;
    }

    /**
     * Initialize the auth session. Failures return an unavailable result so
     * callers never mount an interactive widget without a verified session.
     */
    async initialize() {
        if (this._initialized) {
            return { entraOrgId: this._entraOrgId };
        }

        if (this._isInitializing) {
            await this._waitForInit();
            return this._initialized ? { entraOrgId: this._entraOrgId } : { entraOrgId: null, tenantAvailable: false };
        }

        return this._bootstrap();
    }

    /**
     * Force a fresh session — used after a 401 to cover the case where
     * the bound token's TTL expired or its fingerprint no longer matches
     * (e.g. the user switched networks).
     */
    async reinitialize() {
        this._initialized = false;
        this._sessionToken = null;
        return this._bootstrap();
    }

    async _bootstrap() {
        this._isInitializing = true;

        try {
            const tokenUrl = configService.getAnonTokenUrl();
            if (!tokenUrl) {
                logger.error('Token URL not configured');
                return { entraOrgId: null, tenantAvailable: false };
            }

            const councilValue = configService.getCouncilFromUrl() || '';

            const response = await fetch(tokenUrl, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ councilValue })
            });

            if (!response.ok) {
                if (response.status === 403 || response.status === 503) {
                    const payload = await response.json().catch(() => null);
                    if (payload?.error === 'TENANT_SUSPENDED' || payload?.error === 'TENANT_AVAILABILITY_UNAVAILABLE') {
                        logger.warn('Tenant access is temporarily unavailable');
                        return { entraOrgId: null, tenantAvailable: false };
                    }
                }
                throw new Error(`Auth init request failed: ${response.status}`);
            }

            const result = await response.json();
            const entraOrgId = result.entraOrgId;
            const sessionToken = result.sessionToken;

            if (entraOrgId) {
                sessionStore.set(STORAGE_KEYS.ENTRA_ORG_ID, entraOrgId);
                this._entraOrgId = entraOrgId;
            }

            // In-memory only — never persisted. A bound token survives a
            // page reload anyway because /api/auth/token will re-mint one
            // on the next request.
            if (sessionToken) {
                this._sessionToken = sessionToken;
            }

            this._initialized = true;
            logger.info('Auth session initialized');
            return { entraOrgId };
        } catch (error) {
            logger.error('Error initializing auth session:', error.message);
            return { entraOrgId: null, tenantAvailable: false };
        } finally {
            this._isInitializing = false;
        }
    }

    getEntraOrgId() {
        return this._entraOrgId || sessionStore.get(STORAGE_KEYS.ENTRA_ORG_ID) || null;
    }

    /**
     * @returns {string|null} current bound session token, or null if not yet issued
     */
    getSessionToken() {
        return this._sessionToken;
    }

    /**
     * Header object to merge into every `/api/proxy/*` request.
     */
    getSessionHeaders() {
        return this._sessionToken ? { 'x-daisy-session': this._sessionToken } : {};
    }

    _waitForInit() {
        return new Promise((resolve) => {
            const checkInterval = setInterval(() => {
                if (!this._isInitializing) {
                    clearInterval(checkInterval);
                    resolve();
                }
            }, 100);
        });
    }
}

export const authService = new AuthService();
